import type { WorkflowNode } from '../../../types/backend';
import { byLayerOf, fallsThroughTo, routesFrom, routesInto } from './workflowGraph';

// A DRAKON canvas per stage. Structure and coordinates, no React and no DOM, so the drawing can
// be checked by reading numbers.
//
// DRAKON draws a silhouette as branches standing side by side, which is right on paper and wrong
// in a pane: one row of branches is as wide as the whole algorithm, and a drawing that scrolls
// sideways is one nobody reads whole. The icons were never the problem. So each stage keeps its
// own small silhouette, with its own header and its own exits, and the stages stack downward.
// A canvas is then only ever as wide as a single stage needs, and the page scrolls the way a
// page is supposed to.
//
// A jump between canvases is not drawn. It is an Address icon you can click, which lands you on
// the stage it names. An arrow can only be followed forwards and only by eye; this can be
// followed both ways, and a stage can say who reaches it as easily as where it goes.

export const ICON_W = 208;
export const DENSE_ICON_W = 158;
export const ICON_MIN_H = 48;
export const GAP_Y = 26;
export const HEADER_H = 30;
export const ADDRESS_H = 36;
export const ADDRESS_W = 150;
export const DENSE_ADDRESS_W = 128;
export const BAR_H = 5;
export const LINE_H = 15;
export const STEP_GAP = 20;
const CHAR = 6.3;
const LABEL_CHAR = 5.6;
const PAD_Y = 18;
const MAX_LINES = 3;
const PAD_X = 10;

export type IconKind =
  | 'header'
  | 'step'
  | 'gate'
  | 'decision'
  | 'address'
  | 'inlet'
  | 'end'
  | 'bar';

export interface Icon {
  id: string;
  kind: IconKind;
  /** The step this icon stands for. Structural icons have none. */
  node: WorkflowNode | null;
  x: number;
  y: number;
  w: number;
  h: number;
  lines: string[];
  caption: string | null;
  /** For an Address icon the stage it sends the run to; for an inlet, the stage it comes from. */
  target: number | null;
  /** For an Address icon: the condition that takes this route. */
  condition: string | null;
}

export interface Connector {
  id: string;
  d: string;
  lines: string[];
  condition: string | null;
  labelX: number;
  labelY: number;
  labelW: number;
  labelH: number;
}

export interface StageCanvas {
  layer: number;
  icons: Icon[];
  connectors: Connector[];
  w: number;
  h: number;
}

export const wrapText = (
  text: string,
  width: number,
  maxLines: number,
  charWidth = CHAR,
): string[] => {
  const perLine = Math.max(6, Math.floor(width / charWidth));
  const out: string[] = [];
  let line = '';
  text
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .forEach((word) => {
      const next = line ? `${line} ${word}` : word;
      if (next.length <= perLine) {
        line = next;
        return;
      }
      if (line) out.push(line);
      line = word.length > perLine ? `${word.slice(0, perLine - 1)}…` : word;
    });
  if (line) out.push(line);
  if (out.length === 0) return [''];
  if (out.length <= maxLines) return out;
  return [...out.slice(0, maxLines - 1), `${out[maxLines - 1].slice(0, perLine - 2)}…`];
};

const widest = (lines: string[], charWidth: number) =>
  Math.max(0, ...lines.map((l) => l.length * charWidth));

export interface StageOptions {
  dense?: boolean;
  captionFor?: (node: WorkflowNode) => string | null;
}

/** One stage, drawn as its own small silhouette: header, its steps, its exits. */
export const layoutStage = (
  layer: number,
  nodes: WorkflowNode[],
  options: StageOptions = {},
): StageCanvas => {
  const { dense = false, captionFor } = options;
  const iconW = dense ? DENSE_ICON_W : ICON_W;
  const addressW = dense ? DENSE_ADDRESS_W : ADDRESS_W;

  const members = byLayerOf(nodes).get(layer) ?? [];
  const routes = routesFrom(layer, nodes);
  const parallel = members.length > 1;

  const bodyOf = (node: WorkflowNode) => {
    const caption = captionFor ? captionFor(node) : null;
    const isDecision = node.kind === 'decision';
    const lines = wrapText(
      node.label || 'Untitled step',
      isDecision ? iconW - 46 : iconW - 24,
      MAX_LINES,
    );
    const h = Math.max(ICON_MIN_H, lines.length * LINE_H + (caption ? LINE_H : 0) + PAD_Y);
    return { lines, caption, h };
  };

  const bodies = members.map(bodyOf);
  const stepsW = members.length * iconW + (members.length - 1) * STEP_GAP;
  const tallest = Math.max(ICON_MIN_H, ...bodies.map((b) => b.h));

  // Each exit keeps a lane of its own, wide enough for its icon and its condition.
  const exitPitch = Math.max(addressW + 22, dense ? 150 : 186);
  const exitsW = routes.length * exitPitch;

  const incomingCount = routesInto(layer, nodes).filter(
    (r) => fallsThroughTo(r.from, nodes) !== layer,
  ).length;
  const inletsWidth = incomingCount * Math.max(addressW + 22, dense ? 150 : 186);
  const w = Math.max(stepsW, exitsW, inletsWidth, iconW) + PAD_X * 2;
  const mid = w / 2;

  // What can send a run here, drawn the same way as what this stage can send one to: the icon
  // at the foot of one canvas and the icon at the head of another are the two ends of the same
  // jump, so they are the same icon. Said in a line of prose instead, a stage with three ways in
  // read as a footnote rather than as part of the drawing.
  const incoming = routesInto(layer, nodes).filter((r) => fallsThroughTo(r.from, nodes) !== layer);
  const inletPitch = Math.max(addressW + 22, dense ? 150 : 186);
  const inletLines = incoming.map((r) =>
    r.condition ? wrapText(r.condition, inletPitch - 20, 2, LABEL_CHAR) : [],
  );
  const inletLabelBand =
    incoming.length === 0 ? 0 : Math.max(0, ...inletLines.map((l) => l.length * 13 + 24));
  const inletBand = incoming.length === 0 ? 0 : ADDRESS_H + inletLabelBand + GAP_Y;

  const icons: Icon[] = [];
  const connectors: Connector[] = [];

  let y = 4;

  if (incoming.length > 0) {
    const inletTop = y;
    const crossY = inletTop + inletBand - GAP_Y;
    incoming.forEach((route, i) => {
      const ix = mid - (incoming.length * inletPitch) / 2 + inletPitch * i + inletPitch / 2;
      const lines = inletLines[i];
      icons.push({
        id: `inlet-${layer}-${route.from}-${i}`,
        kind: 'inlet',
        node: null,
        x: ix - addressW / 2,
        y: inletTop,
        w: addressW,
        h: ADDRESS_H,
        lines: [`Stage ${route.from}`],
        caption: null,
        target: route.from,
        condition: route.condition,
      });
      connectors.push({
        id: `in-${layer}-${route.from}-${i}`,
        d:
          Math.abs(ix - mid) < 0.5
            ? `M ${ix} ${inletTop + ADDRESS_H} L ${ix} ${crossY}`
            : [
                `M ${ix} ${inletTop + ADDRESS_H}`,
                `L ${ix} ${crossY - 14}`,
                `L ${mid} ${crossY - 14}`,
                `L ${mid} ${crossY}`,
              ].join(' '),
        lines,
        condition: route.condition,
        labelX: ix,
        labelY: inletTop + ADDRESS_H + (crossY - 14 - inletTop - ADDRESS_H) / 2,
        labelW: Math.max(widest(lines, LABEL_CHAR) + 12, 0),
        labelH: lines.length * 13 + 6,
      });
    });
    y += inletBand;
  }

  icons.push({
    id: `header-${layer}`,
    kind: 'header',
    node: null,
    x: mid - (dense ? 72 : 88) / 2,
    y,
    w: dense ? 72 : 88,
    h: HEADER_H,
    lines: [`Stage ${layer}`],
    caption: null,
    target: null,
    condition: null,
  });
  y += HEADER_H;

  connectors.push({
    id: `head-${layer}`,
    d: `M ${mid} ${y} L ${mid} ${y + GAP_Y}`,
    lines: [],
    condition: null,
    labelX: 0,
    labelY: 0,
    labelW: 0,
    labelH: 0,
  });
  y += GAP_Y;

  if (parallel) {
    // Every step in a stage runs at once and the stage ends when the slowest is done. That is a
    // fork and a join, and the bars are how it is said.
    icons.push({
      id: `fork-${layer}`,
      kind: 'bar',
      node: null,
      x: mid - stepsW / 2,
      y,
      w: stepsW,
      h: BAR_H,
      lines: [],
      caption: null,
      target: null,
      condition: null,
    });
    y += BAR_H + 12;
  }

  const stepsTop = y;
  members.forEach((node, i) => {
    const { lines, caption, h } = bodies[i];
    const cx = parallel ? mid - stepsW / 2 + i * (iconW + STEP_GAP) + iconW / 2 : mid;
    icons.push({
      id: node.id,
      kind: node.kind === 'decision' ? 'decision' : node.gate ? 'gate' : 'step',
      node,
      x: cx - iconW / 2,
      y: stepsTop,
      w: iconW,
      h,
      lines,
      caption,
      target: null,
      condition: null,
    });
    if (parallel) {
      connectors.push({
        id: `fork-${layer}-${node.id}`,
        d: `M ${cx} ${stepsTop - 12} L ${cx} ${stepsTop}`,
        lines: [],
        condition: null,
        labelX: 0,
        labelY: 0,
        labelW: 0,
        labelH: 0,
      });
      connectors.push({
        id: `join-${layer}-${node.id}`,
        d: `M ${cx} ${stepsTop + h} L ${cx} ${stepsTop + tallest + 12}`,
        lines: [],
        condition: null,
        labelX: 0,
        labelY: 0,
        labelW: 0,
        labelH: 0,
      });
    }
  });
  y = stepsTop + tallest;

  if (parallel) {
    y += 12;
    icons.push({
      id: `join-${layer}`,
      kind: 'bar',
      node: null,
      x: mid - stepsW / 2,
      y,
      w: stepsW,
      h: BAR_H,
      lines: [],
      caption: null,
      target: null,
      condition: null,
    });
    y += BAR_H;
  }

  // The exits, all on one line at the foot of the canvas, the way a silhouette aligns them.
  const stem = y;
  const labelBand = routes.some((r) => r.condition)
    ? Math.max(
        ...routes.map((r) =>
          r.condition ? wrapText(r.condition, exitPitch - 20, 3, LABEL_CHAR).length * 13 + 16 : 0,
        ),
      )
    : 0;
  const exitY = stem + GAP_Y + labelBand;

  routes.forEach((route, i) => {
    const ox =
      routes.length === 1
        ? mid
        : mid - (routes.length * exitPitch) / 2 + exitPitch * i + exitPitch / 2;
    const lines = route.condition ? wrapText(route.condition, exitPitch - 20, 3, LABEL_CHAR) : [];
    const d =
      Math.abs(ox - mid) < 0.5
        ? `M ${mid} ${stem} L ${mid} ${exitY}`
        : [
            `M ${mid} ${stem}`,
            `L ${mid} ${stem + 14}`,
            `L ${ox} ${stem + 14}`,
            `L ${ox} ${exitY}`,
          ].join(' ');

    connectors.push({
      id: `exit-${layer}-${i}`,
      d,
      lines,
      condition: route.condition,
      labelX: ox,
      labelY: stem + 14 + (exitY - stem - 14) / 2,
      labelW: Math.max(widest(lines, LABEL_CHAR) + 12, 0),
      labelH: lines.length * 13 + 6,
    });

    icons.push({
      id: `exit-${layer}-${i}`,
      kind: route.target === null ? 'end' : 'address',
      node: null,
      x: ox - addressW / 2,
      y: exitY,
      w: addressW,
      h: ADDRESS_H,
      lines: [route.target === null ? 'End' : `Stage ${route.target}`],
      caption: null,
      target: route.target,
      condition: route.condition,
    });
  });

  return { layer, icons, connectors, w, h: exitY + ADDRESS_H + 6 };
};
